/**
 * Design showcase — 1:1 port of `Capture Flow Hi-Fi.html` from the handoff
 * bundle. Groups all 26 Android mock-ups into the five sections defined by
 * the designer (Entry, Capture HUD, Timeline, Sync, Review).
 */
import type React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  S01_SessionHub,
  S02_NewSession,
  S03_CaptureModes,
  S04_CameraHUD,
  S05_AnnotatePhoto,
  S06_VoiceRecording,
  S07_VoiceReview,
  S08_QuickNote,
  S09_GPS,
} from '@/screens/hifi/screens-1-9';
import {
  S10_Barcode,
  S11_Timeline,
  S12_CaptureDetail,
  S13_FormOverview,
  S14_FormFill,
  S15_AISuggestions,
  S16_SyncQueue,
  S17_Syncing,
  S18_Conflict,
} from '@/screens/hifi/screens-10-18';
import {
  S19_Review,
  S20_SignOff,
  S21_Success,
  S22_Approval,
  S23_Share,
  S24_Templates,
  S25_Empty,
  S26_Permissions,
} from '@/screens/hifi/screens-19-26';

export const Route = createFileRoute('/design-showcase')({
  component: DesignShowcasePage,
});

interface Section {
  num: string;
  title: string;
  desc: string;
  screens: Array<() => React.JSX.Element>;
}

const sections: Section[] = [
  {
    num: '01',
    title: 'Entry & Session',
    desc: 'First-run permissions, empty state, session hub, new session sheet, and the radial mode picker that anchors Variant D.',
    screens: [S26_Permissions, S25_Empty, S01_SessionHub, S02_NewSession, S03_CaptureModes],
  },
  {
    num: '02',
    title: 'Capture — dark HUD',
    desc: 'Every mode is full-screen and dark. Viewfinder, waveform, map — content first, chrome recedes. AI overlays suggest without blocking.',
    screens: [
      S04_CameraHUD,
      S05_AnnotatePhoto,
      S06_VoiceRecording,
      S07_VoiceReview,
      S08_QuickNote,
      S09_GPS,
      S10_Barcode,
    ],
  },
  {
    num: '03',
    title: 'Timeline & Forms',
    desc: 'Light data screens. Everything captured becomes a timeline event and auto-stitches into the inspection form with source traceability.',
    screens: [S11_Timeline, S12_CaptureDetail, S13_FormOverview, S14_FormFill, S15_AISuggestions],
  },
  {
    num: '04',
    title: 'Offline · Sync · Conflict',
    desc: 'Offline-first is a first-class state — queue, progress, resume. Conflicts surface as a side-by-side merge, not an error.',
    screens: [S16_SyncQueue, S17_Syncing, S18_Conflict],
  },
  {
    num: '05',
    title: 'Review · Sign-off · Handoff',
    desc: 'Pre-submit review, in-app signature, submission receipt, reviewer approval, share sheet, and template management.',
    screens: [S19_Review, S20_SignOff, S21_Success, S22_Approval, S23_Share, S24_Templates],
  },
];

function DesignShowcasePage() {
  return (
    <div className="kap-showcase-bg">
      <div className="mx-auto max-w-[1680px] px-12 pb-32 pt-14">
        <div className="mb-3 flex items-end justify-between gap-10 border-b border-[#d8dde2] pb-5">
          <div>
            <h1 className="m-0 font-sans text-[42px] font-extrabold leading-none tracking-[-0.035em]">
              Kapture AI — Capture Flow
            </h1>
            <div className="text-text-dim mt-1.5 max-w-[640px] text-sm font-normal leading-relaxed">
              Hi-fidelity visual design · Variant D promoted from wireframe. Dark camera HUD for
              in-field capture, light data screens for review & admin. 26 screens, 412×892 Android.
            </div>
          </div>
          <div className="text-text-mute text-right font-mono text-[11px] font-medium uppercase tracking-wider">
            v1.0 · Sarah Chen flow
            <span className="bg-ink ml-1.5 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-white">
              <span className="bg-brand h-1.5 w-1.5 rounded-sm" />
              HI-FI
            </span>
          </div>
        </div>

        <div className="rounded-panel border-stroke bg-surface shadow-card mb-8 flex flex-wrap items-center gap-3.5 border px-4 py-3.5">
          <LegendItem color="#fa5d00" label="Brand · Harvest Flame" />
          <Divider />
          <LegendItem color="#1a1b18" label="Ink · Dark HUD" />
          <Divider />
          <LegendItem color="#2f8f63" label="Good" />
          <LegendItem color="#a97a08" label="Warn" />
          <LegendItem color="#bb2f26" label="Danger" />
          <Divider />
          <div className="text-text-dim inline-flex items-center gap-2 font-mono text-[11px] font-medium">
            Type · MuotoWeb + DM Mono
          </div>
          <Divider />
          <div className="text-text-dim inline-flex items-center gap-2 font-mono text-[11px] font-medium">
            Frame · Android 412×892
          </div>
        </div>

        {sections.map((s) => (
          <section key={s.num}>
            <div className="mb-5 mt-12 flex items-center gap-4">
              <div className="bg-ink rounded-full px-2.5 py-1 font-mono text-xs font-semibold tracking-wider text-white">
                {s.num}
              </div>
              <div className="text-[22px] font-bold tracking-[-0.02em]">{s.title}</div>
              <div className="text-text-dim ml-1 flex-1 border-l border-[#d8dde2] pl-4 text-sm leading-relaxed">
                {s.desc}
              </div>
            </div>
            <div className="grid grid-cols-1 items-start gap-x-6 gap-y-10 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {s.screens.map((Screen, i) => (
                <div key={i}>
                  <Screen />
                </div>
              ))}
            </div>
          </section>
        ))}

        <div className="text-text-mute mt-16 flex justify-between border-t border-[#d8dde2] pt-6 font-mono text-xs uppercase tracking-wider">
          <div>Kapture AI · Capture Flow · Hi-Fi · Variant D</div>
          <div>26 screens · 5 sections · Android 412×892</div>
        </div>
      </div>
    </div>
  );
}

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <div className="text-text-dim inline-flex items-center gap-2 text-xs font-medium">
      <div className="h-3 w-3 rounded-[3px]" style={{ background: color }} />
      {label}
    </div>
  );
}

function Divider() {
  return <div className="bg-stroke h-5 w-px" />;
}
